import { useEffect, useRef } from "react";

declare global {
  interface Window {
    turnstile?: {
      render: (element: HTMLElement, options: Record<string, unknown>) => string;
      remove: (id: string) => void;
    };
  }
}

const sitekey = process.env.REACT_APP_TURNSTILE_SITE_KEY;

export function Turnstile({ onToken, onError }: { onToken: (token: string) => void; onError: (message: string) => void }) {
  const container = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let widget: string | undefined;
    let disposed = false;
    if (!sitekey) {
      onError("The contact form is temporarily unavailable. Please email support@medcoreresearch.com.");
      return;
    }
    const render = () => {
      if (disposed || !container.current || !window.turnstile || widget !== undefined) return;
      widget = window.turnstile.render(container.current, {
        sitekey,
        action: "contact",
        theme: "light",
        size: "flexible",
        callback: onToken,
        "expired-callback": () => onToken(""),
        "error-callback": () => {
          onToken("");
          onError("Please refresh the security check or email support@medcoreresearch.com.");
        },
      });
    };
    let script = document.querySelector<HTMLScriptElement>('script[data-medcore-turnstile]');
    if (!script) {
      script = document.createElement("script");
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.dataset.medcoreTurnstile = "true";
      document.head.appendChild(script);
    }
    const failed = () => onError("Please allow the security check to load, or email support@medcoreresearch.com.");
    script.addEventListener("load", render);
    script.addEventListener("error", failed);
    render();
    const timeout = window.setTimeout(() => { if (widget === undefined) failed(); }, 15000);
    return () => {
      disposed = true;
      window.clearTimeout(timeout);
      script?.removeEventListener("load", render);
      script?.removeEventListener("error", failed);
      if (widget !== undefined) window.turnstile?.remove(widget);
    };
  }, [onToken, onError]);
  return <div ref={container} className="min-h-[65px]" />;
}
